"use client";

import type { ScaleLinear } from "d3-scale";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import type { LineConfig, Margin, TooltipData } from "./chart-context";
import { localPointFromSvg } from "./scatter-svg";
import type { ChartSelection } from "./use-chart-interaction";
import { useScheduledTooltip } from "./use-scheduled-tooltip";
import {
  type AnyXScale,
  isBandScale,
  type XRawValue,
  type XScaleKind,
} from "./x-scale";
import { normalizeYAxisId } from "./y-axis-scales";

type YScale = ScaleLinear<number, number>;

export type ScatterInteractionMode = "x" | "point";

interface UseScatterChartInteractionParams {
  xScale: AnyXScale;
  xScaleKind: XScaleKind;
  yScale: YScale;
  yScales: Record<string, YScale>;
  data: Record<string, unknown>[];
  lines: LineConfig[];
  margin: Margin;
  xAccessor: (d: Record<string, unknown>) => XRawValue;
  bisectX:
    | ((data: Record<string, unknown>[], target: number, lo: number) => number)
    | null;
  canInteract: boolean;
  mode?: ScatterInteractionMode;
}

interface ScatterChartInteractionResult {
  tooltipData: TooltipData | null;
  setTooltipData: React.Dispatch<React.SetStateAction<TooltipData | null>>;
  clearTooltip: () => void;
  selection: ChartSelection | null;
  clearSelection: () => void;
  pointEnter: (tooltip: TooltipData, key: string) => void;
  pointLeave: () => void;
  pointTap: (
    tooltip: TooltipData,
    key: string,
    container: SVGGElement | null
  ) => void;
  resetPointState: () => void;
  interactionHandlers: {
    onMouseMove?: (event: React.MouseEvent<SVGGElement>) => void;
    onMouseLeave?: () => void;
    onMouseDown?: (event: React.MouseEvent<SVGGElement>) => void;
    onMouseUp?: () => void;
    onTouchStart?: (event: React.TouchEvent<SVGGElement>) => void;
    onTouchMove?: (event: React.TouchEvent<SVGGElement>) => void;
    onTouchEnd?: () => void;
    onTouchCancel?: () => void;
  };
  interactionStyle: React.CSSProperties;
}

export function useScatterChartInteraction({
  xScale,
  xScaleKind,
  yScale,
  yScales,
  data,
  lines,
  margin,
  xAccessor,
  bisectX,
  canInteract,
  mode = "x",
}: UseScatterChartInteractionParams): ScatterChartInteractionResult {
  const [selection, setSelection] = useState<ChartSelection | null>(null);
  const hoverCapable = useHoverCapable();
  const {
    tooltipData,
    setTooltipData,
    scheduleTooltip,
    clearTooltip,
    resetTooltipDedupe,
  } = useScheduledTooltip<TooltipData>();

  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef<number>(0);
  const lastTappedIndexRef = useRef<number | null>(null);
  const touchTooltipVisibleRef = useRef(false);
  const containerElRef = useRef<SVGGElement | null>(null);
  const pendingLeaveRef = useRef<number | null>(null);
  const lastTappedPointRef = useRef<string | null>(null);

  const resolveTooltipFromX = useCallback(
    (pixelX: number): TooltipData | null => {
      if (!bisectX || isBandScale(xScale)) return null;
      const invertedRaw = xScale.invert(pixelX);
      const x0 = Number(invertedRaw);
      const index = bisectX(data, x0, 1);
      const d0 = data[index - 1];
      const d1 = data[index];

      if (!d0) {
        return null;
      }

      let d = d0;
      let finalIndex = index - 1;
      if (d1) {
        const d0Time = Number(xAccessor(d0));
        const d1Time = Number(xAccessor(d1));
        if (x0 - d0Time > d1Time - x0) {
          d = d1;
          finalIndex = index;
        }
      }

      const yPositions: Record<string, number> = {};
      for (const line of lines) {
        const value = d[line.dataKey];
        if (typeof value === "number") {
          const axisScale = yScales[normalizeYAxisId(line.yAxisId)] ?? yScale;
          yPositions[line.dataKey] = axisScale(value) ?? 0;
        }
      }

      const xRaw = xAccessor(d);
      const xPos = isBandScale(xScale) ? 0 : (xScale(xRaw as never) ?? 0);

      return {
        point: d,
        index: finalIndex,
        x: xPos,
        yPositions,
      };
    },
    [xScale, yScale, yScales, data, lines, xAccessor, bisectX],
  );

  const resolveIndexFromX = useCallback(
    (pixelX: number): number => {
      if (!bisectX || isBandScale(xScale)) return 0;
      const invertedRaw = xScale.invert(pixelX);
      const x0 = Number(invertedRaw);
      const index = bisectX(data, x0, 1);
      const d0 = data[index - 1];
      const d1 = data[index];
      if (!d0) {
        return 0;
      }
      if (d1) {
        const d0Time = Number(xAccessor(d0));
        const d1Time = Number(xAccessor(d1));
        if (x0 - d0Time > d1Time - x0) {
          return index;
        }
      }
      return index - 1;
    },
    [xScale, data, xAccessor, bisectX],
  );

  const getChartX = useCallback(
    (
      event: React.MouseEvent<SVGGElement> | React.TouchEvent<SVGGElement>,
      touchIndex = 0
    ): number | null => {
      const svg = event.currentTarget.ownerSVGElement;
      let clientX: number;
      let clientY: number;

      if ("touches" in event) {
        const touch = event.touches[touchIndex];
        if (!touch) {
          return null;
        }
        clientX = touch.clientX;
        clientY = touch.clientY;
      } else {
        clientX = event.clientX;
        clientY = event.clientY;
      }

      const point = localPointFromSvg(svg, clientX, clientY);
      if (!point) {
        return null;
      }
      return point.x - margin.left;
    },
    [margin.left]
  );

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<SVGGElement>) => {
      const chartX = getChartX(event);
      if (chartX === null) {
        return;
      }

      if (isDraggingRef.current) {
        const startX = Math.min(dragStartXRef.current, chartX);
        const endX = Math.max(dragStartXRef.current, chartX);
        setSelection({
          startX,
          endX,
          startIndex: resolveIndexFromX(startX),
          endIndex: resolveIndexFromX(endX),
          active: true,
        });
        return;
      }

      const tooltip = resolveTooltipFromX(chartX);
      if (tooltip) {
        scheduleTooltip(tooltip);
      }
    },
    [getChartX, resolveTooltipFromX, resolveIndexFromX, scheduleTooltip]
  );

  const handleMouseLeave = useCallback(() => {
    clearTooltip();
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
    }
    setSelection(null);
  }, [clearTooltip]);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent<SVGGElement>) => {
      const chartX = getChartX(event);
      if (chartX === null) {
        return;
      }
      isDraggingRef.current = true;
      dragStartXRef.current = chartX;
      clearTooltip();
      setSelection(null);
    },
    [getChartX, clearTooltip]
  );

  const handleMouseUp = useCallback(() => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
    }
    setSelection(null);
  }, []);

  const handleTouchStart = useCallback(
    (event: React.TouchEvent<SVGGElement>) => {
      containerElRef.current = event.currentTarget;
      if (event.touches.length === 1) {
        const chartX = getChartX(event, 0);
        if (chartX === null) {
          return;
        }
        const tooltip = resolveTooltipFromX(chartX);
        if (tooltip) {
          if (
            touchTooltipVisibleRef.current &&
            lastTappedIndexRef.current === tooltip.index
          ) {
            clearTooltip();
            touchTooltipVisibleRef.current = false;
            lastTappedIndexRef.current = null;
            return;
          }
          scheduleTooltip(tooltip);
          touchTooltipVisibleRef.current = true;
          lastTappedIndexRef.current = tooltip.index;
        }
      } else if (event.touches.length === 2) {
        resetTooltipDedupe();
        clearTooltip();
        touchTooltipVisibleRef.current = false;
        lastTappedIndexRef.current = null;
        const x0 = getChartX(event, 0);
        const x1 = getChartX(event, 1);
        if (x0 === null || x1 === null) {
          return;
        }
        const startX = Math.min(x0, x1);
        const endX = Math.max(x0, x1);
        setSelection({
          startX,
          endX,
          startIndex: resolveIndexFromX(startX),
          endIndex: resolveIndexFromX(endX),
          active: true,
        });
      }
    },
    [
      getChartX,
      resolveTooltipFromX,
      resolveIndexFromX,
      scheduleTooltip,
      resetTooltipDedupe,
      clearTooltip,
    ]
  );

  const handleTouchMove = useCallback(
    (event: React.TouchEvent<SVGGElement>) => {
      if (event.touches.length === 2) {
        const x0 = getChartX(event, 0);
        const x1 = getChartX(event, 1);
        if (x0 === null || x1 === null) {
          return;
        }
        const startX = Math.min(x0, x1);
        const endX = Math.max(x0, x1);
        setSelection({
          startX,
          endX,
          startIndex: resolveIndexFromX(startX),
          endIndex: resolveIndexFromX(endX),
          active: true,
        });
      }
    },
    [getChartX, resolveIndexFromX]
  );

  const handleTouchEnd = useCallback(() => {
    setSelection(null);
  }, []);

  const clearSelection = useCallback(() => {
    setSelection(null);
  }, []);

  const cancelPendingLeave = useCallback(() => {
    if (pendingLeaveRef.current !== null) {
      cancelAnimationFrame(pendingLeaveRef.current);
      pendingLeaveRef.current = null;
    }
  }, []);

  useEffect(() => cancelPendingLeave, [cancelPendingLeave]);

  const pointEnter = useCallback(
    (tooltip: TooltipData, key: string) => {
      cancelPendingLeave();
      scheduleTooltip(tooltip, key);
    },
    [cancelPendingLeave, scheduleTooltip]
  );

  const pointLeave = useCallback(() => {
    cancelPendingLeave();
    pendingLeaveRef.current = requestAnimationFrame(() => {
      pendingLeaveRef.current = null;
      clearTooltip();
    });
  }, [cancelPendingLeave, clearTooltip]);

  const pointTap = useCallback(
    (tooltip: TooltipData, key: string, container: SVGGElement | null) => {
      containerElRef.current = container;
      cancelPendingLeave();
      if (
        touchTooltipVisibleRef.current &&
        lastTappedPointRef.current === key
      ) {
        clearTooltip();
        touchTooltipVisibleRef.current = false;
        lastTappedPointRef.current = null;
        return;
      }
      scheduleTooltip(tooltip, key);
      touchTooltipVisibleRef.current = true;
      lastTappedPointRef.current = key;
    },
    [cancelPendingLeave, clearTooltip, scheduleTooltip]
  );

  const resetPointState = useCallback(() => {
    cancelPendingLeave();
    clearTooltip();
    setSelection(null);
    isDraggingRef.current = false;
    touchTooltipVisibleRef.current = false;
    lastTappedIndexRef.current = null;
    lastTappedPointRef.current = null;
  }, [cancelPendingLeave, clearTooltip]);

  useEffect(() => {
    if (!canInteract) return;
    const onDocPointerDown = (event: PointerEvent) => {
      if (event.pointerType !== "touch") return;
      if (!touchTooltipVisibleRef.current) return;
      const container = containerElRef.current;
      if (!container) return;
      const target = event.target as Node | null;
      if (target && container.contains(target)) return;
      clearTooltip();
      touchTooltipVisibleRef.current = false;
      lastTappedIndexRef.current = null;
      lastTappedPointRef.current = null;
    };
    document.addEventListener("pointerdown", onDocPointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", onDocPointerDown, true);
    };
  }, [canInteract, clearTooltip]);

  const xInteraction = canInteract && mode === "x";

  const interactionHandlers = xInteraction
    ? {
        ...(hoverCapable
          ? {
              onMouseMove: handleMouseMove,
              onMouseLeave: handleMouseLeave,
              onMouseDown: handleMouseDown,
              onMouseUp: handleMouseUp,
            }
          : {}),
        onTouchStart: handleTouchStart,
        onTouchMove: handleTouchMove,
        onTouchEnd: handleTouchEnd,
        // A two-finger drag now starts a browser pinch, which ends the sequence
        // with touchcancel instead of touchend. Without this the range
        // selection stays on screen with active: true.
        onTouchCancel: handleTouchEnd,
      }
    : {};

  const interactionStyle: React.CSSProperties = {
    cursor: xInteraction ? "crosshair" : "default",
    touchAction: "manipulation",
  };

  return {
    tooltipData,
    setTooltipData,
    clearTooltip,
    selection,
    clearSelection,
    pointEnter,
    pointLeave,
    pointTap,
    resetPointState,
    interactionHandlers,
    interactionStyle,
  };
}
